import './AddVenue.scss';
import 'react-toastify/dist/ReactToastify.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PlacesAutocomplete, { geocodeByPlaceId } from '../../components/PlacesAutocomplete/PlacesAutocomplete';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { v4 } from 'uuid';
import * as Yup from 'yup';

import chevronLeft from '../../assets/images/icons/chevron-left.svg';
import { HoursOfOperation } from '../../components/HoursOfOperation';
import InputForm from '../../components/InputForm/InputForm';
import SelectForm from '../../components/SelectForm/SelectForm';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from '../../components/Buttons/Button';
import PhotoGallery from '../../components/PhotoGallery';
import { arrayMove } from '@dnd-kit/sortable';
import '../../components/VenueInformation/VenueInformation.scss';

const SectionCard = ({ title, children }) => (
  <div className="venue-section-card" style={{ marginBottom: 16 }}>
    <h3 className="venue-section-card__title">{title}</h3>
    <div className="venue-section-card__body">{children}</div>
  </div>
);

const TwoCol = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
    {children}
  </div>
);


const AddVenue = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Which market tab launched this form. NYC venues are staged: tagged
  // market "nyc" and created inactive so they never reach the consumer app.
  const market = searchParams.get('market') === 'nyc' ? 'nyc' : 'miami';

  const [additionalHours, setAdditionalHours] = useState([]);
  const [updatedHour, setUpdatedHour] = useState();
  const [loading, setLoading] = useState(false);

  const [updatedName, setUpdatedName] = useState("");
  const [updatedCat, setUpdatedCat] = useState([]);
  const [updatedAddress, setUpdatedAddress] = useState("");

  const [updatedUrl, setUpdatedUrl] = useState("");
  const [updatedEmail, setUpdatedEmail] = useState("");
  // const [updatedPrice, setUpdatedPrice] = useState("");
  const [updatedAbout, setUpdatedAbout] = useState("");
  const [updatedPhone, setUpdatedPhone] = useState("");
  const [updatedLat, setUpdatedLat] = useState("");
  const [updatedLong, setUpdatedLong] = useState("");
  const [updatedDress, setUpdatedDress] = useState();
  const [updatedMusic, setUpdatedMusic] = useState([]);

  const [updateState, setUpdateState] = useState("");
  const [updateCity, setUpdateCity] = useState("");
  const [updateCountry, setUpdateCountry] = useState("");
  const [updateMapIcon, setUpdateMapIcon] = useState("");
  const [updatedLocation, setUpdatedLocation] = useState("");
  const [locationsOptions, setLocationOptions] = useState([]);

  const [updateSpecials, setUpdateSpecials] = useState("");

  const [venueImages, setVenueImages] = useState([]);

  const [placeId, setPlaceId] = useState();

  const [errors, setErrors] = useState({});

  const handleFilesPicked = useCallback((files) => {
    const list = Array.from(files).filter((f) => f.type?.startsWith("image/"));
    if (!list.length) return;
    const newPhotos = list.map((file) => ({
      id: `local-${v4()}`,
      url: URL.createObjectURL(file),
      status: "confirmed",
      file,
    }));
    setVenueImages((prev) => [...prev, ...newPhotos]);
  }, []);

  const handleImageRemove = useCallback((id) => {
    setVenueImages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.url?.startsWith("blob:")) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const handleReorder = useCallback((next) => setVenueImages(next), []);

  const handleSetMain = useCallback((id) => {
    setVenueImages((prev) => {
      const currentIndex = prev.findIndex((p) => p.id === id);
      if (currentIndex <= 0) return prev;
      return arrayMove(prev, currentIndex, 0);
    });
  }, []);


  const validationSchema = Yup.object().shape({
    updatedName: Yup.string().required("Name is Required"),
    updatedCat: Yup.array().of(Yup.string()).min(1, "Select at least one category"),
    updatedAddress: Yup.string().required("Select a location"),
    // Phone + email are optional. Some venues genuinely don't publish them.
    // Leave blank instead of typing placeholder data (000-000-0000,
    // email@email.com, etc.) which pollutes the DB. If provided, must match
    // the format. Known placeholder values are rejected with a hint. Empty
    // strings are normalized to undefined below so Mongo stores nothing
    // rather than "".
    updatedPhone: Yup.string().notOneOf(
      ['000-000-0000', '(000) 000-0000', '0000000000', '000', 'n/a', 'na', 'none'],
      "Looks like placeholder data, leave blank instead"
    ),
    updatedUrl: Yup.string().required("Website is Required"),
    updatedEmail: Yup.string().email("Must be a valid email").notOneOf(
      ['email@email.com', 'none@none.com', 'no@email.com', 'n/a@n/a.com'],
      "Looks like placeholder data, leave blank instead"
    ),
    updatedAbout: Yup.string().required("About is Required"),
    // updateSpecials: Yup.string().required("Specials is Required"),
    updatedDress: Yup.string().required("Select dress code"),
    updatedMusic: Yup.array().of(Yup.string()).min(1, "Select at least one music type"),
  });

  const handleAddVenue = async (event) => {
    event.preventDefault();

    // Parse hours from additionalHours
    const parsedHours = additionalHours.reduce((acc, cur) => {
      const { day, hours } = cur;
      acc[day] = hours;
      return acc;
    }, {});

    if (updatedHour) parsedHours[updatedHour.day] = updatedHour.hours;

    try {
      // Yup validation schema
      await validationSchema.validate(
          {
            updatedName,
            updatedCat,
            updatedAddress,
            updatedPhone,
            updatedUrl,
            updatedEmail,
            updatedAbout,
            updatedDress,
            updatedMusic,
          },
          { abortEarly: false }
      );

      // Construct newVenue object. Normalize blank phone + email to
      // undefined so Mongo stores nothing (effectively NULL) rather than
      // an empty string that pollutes downstream queries and analytics.
      const newVenue = {
        name: updatedName,
        tags: updatedCat,
        address: updatedAddress,
        hours: parsedHours,
        url: updatedUrl,
        email: updatedEmail?.trim() || undefined,
        phone: updatedPhone?.trim() || undefined,
        lat: updatedLat,
        lng: updatedLong,
        dress: updatedDress,
        music: updatedMusic,
        description: updatedAbout,
        state: updateState,
        city: updateCity,
        country: updateCountry,
        // marker_icon: updateMapIcon,
        specials: updateSpecials,
        local: updatedLocation,
        place_id: placeId,
        market: market,
        // NYC is pre-launch: stage new venues hidden from the consumer app.
        ...(market === 'nyc' ? { status: 'inactive' } : {}),
      };

      // Function to add the new venue
      const addVenue = async () => {
        try {
          setLoading(true);
          const { data: addedVenue } = await api.post("/venues", newVenue);

          // Handle venue image upload (only if user picked any photos)
          const photosWithFile = venueImages.filter((img) => img.file);
          if (photosWithFile.length > 0) {
            const formData = new FormData();
            photosWithFile.forEach((img) => formData.append("images", img.file));
            await api.post(`/venues/images/${addedVenue._id}`, formData);
          }

          setLoading(false);
          toast.success("New Venue added successfully.");
          navigate(market === 'nyc' ? "/nyc-venues" : "/business");
        } catch (error) {
          console.error("Error adding venue or uploading images: ", error);

          setLoading(false);
          // Handle specific error responses
          if (error.response && error.response.data?.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error("An error occurred while adding the venue.");
          }
        }
      };

      // Call the addVenue function
      await addVenue();

    } catch (error) {
      // Handle Yup validation errors
      if (error instanceof Yup.ValidationError) {
        const validationErrors = error.inner.reduce((acc, err) => {
          acc[err.path] = err.message;
          return acc;
        }, {});
        setErrors(validationErrors);
        toast.error(`Validation Error: ${error.message}`);
      } else {
        console.error("An error occurred: ", error);
        toast.error(`Error adding new Venue. Please try again, ${error.message}`);
      }
    }
  };

  const markeIcon = [
    { label: "Bar", value: "bar" },
    { label: "Club", value: "club" },
    { label: "Lounge", value: "lounge" },
  ];


  const handleCategoryChange = (selectedOptions) => {
    const selectedValues = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => option.value)
      : [];

    setUpdatedCat(selectedValues);
  };


  const handleDressChange = (selectedOption) => {
    setUpdatedDress(selectedOption ? selectedOption.value.trim() : ""); // Set updatedDress directly to the selected value
  };
  const handleMapIcon = (selectedOptions) => {
    setUpdateMapIcon(selectedOptions ? selectedOptions.value : "");
  };

  const handleMusicChange = (selectedOption) => {
    const selectedValues = Array.isArray(selectedOption)
      ? selectedOption.map((option) => option.value)
      : [];

    setUpdatedMusic(selectedValues);
  };


  const handleSelect = async (result) => {
    const placeDetails = await geocodeByPlaceId(result.value.place_id);
    const {
      address_components,
      formatted_address,
      geometry: {
        location: { lat, lng },
      },
      // Newly surfaced from server PR — auto-fill the form fields the
      // admin used to type by hand. Empty fallback for venues Google
      // doesn't carry (rare for established Miami venues, common for
      // a brand-new spot).
      name,
      formatted_phone_number,
      website,
      editorial_summary,
    } = placeDetails[0];

    const city = address_components.find((address) =>
      address.types.some((type) => type === "administrative_area_level_2")
    );
    const state = address_components.find((address) =>
      address.types.some((type) => type === "administrative_area_level_1")
    );
    const country = address_components.find((address) =>
      address.types.some((type) => type === "country")
    );

    if (city) setUpdateCity(city.long_name);
    if (state) setUpdateState(state.short_name);
    if (country) setUpdateCountry(country.short_name);

    setUpdatedAddress(formatted_address);
    setUpdatedLat(lat);
    setUpdatedLong(lng);
    setPlaceId(result.value.place_id);

    // Prefill fields Google already has. Admin can still overwrite any
    // of them before submit. Only prefill when the field is currently
    // empty so we don't trample an edit the admin already typed.
    if (name && !updatedName) setUpdatedName(name);
    if (formatted_phone_number && !updatedPhone) {
      setUpdatedPhone(formatted_phone_number);
    }
    if (website && !updatedUrl) setUpdatedUrl(website);
    if (editorial_summary && !updatedAbout) {
      setUpdatedAbout(editorial_summary);
    }
  };

  const googlePlacesTypesToFetch = ["establishment"];

  const [, setFilters] = useState();

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [dressCodeOptions, setDressCodeOptions] = useState([]);
  const [musicOptions, setMusicOptions] = useState([]);

  useEffect(() => {
    async function getFilters() {
      try {
        const response =  await api.get('/filters', {
          params: { includeAll: true },
        });

        const tags = response.data.tags;
        const dressCode = response.data.dress_code
        const music = response.data.music;
        const locations = response.data.location;

        const formattedTags = tags.map(tag => ({ value: tag, label: tag }));
        const formattedDressCode = dressCode.map(tag => ({ value: tag, label: tag }));
        const formattedMusic = music.map(tag => ({ value: tag, label: tag }));
        const locationOptions = locations.map(tag => ({ value: tag, label: tag }));

        setCategoryOptions(formattedTags);
        setDressCodeOptions(formattedDressCode);
        setMusicOptions(formattedMusic)
        setLocationOptions(locationOptions);
        setFilters(response.data)
      } catch (err) {
        console.log(err)
      }
    }

    getFilters()
  }, [])

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="add-venue">
            <div className="add-venue__top">
              <Link to="/business">
                <img
                  src={chevronLeft}
                  className="add-venue__top-icon"
                  alt="chevron pointing left"
                ></img>
              </Link>
              <h1 className="add-venue__top-heading">Adding new business</h1>
            </div>

            <form onSubmit={handleAddVenue} className="add-venue__form">
              <section className="add-venue__form--left">
                <SectionCard title="Location">
                  <label style={{ padding: "0.5vw 16px 6px", display: "flex", gap: "10px", alignItems: "center", fontSize: "14px", color: "rgba(255, 255, 255, 0.5)" }}>
                    Address (enter name of venue)
                    <span className='error-message'>{errors.updatedAddress}</span>
                  </label>
                  <PlacesAutocomplete
                    autocompletionRequest={{ types: googlePlacesTypesToFetch }}
                    selectProps={{
                      onChange: handleSelect,
                      styles: {
                        control: (provided) => ({
                          ...provided,
                          background: "rgba(255, 255, 255, 0.1)",
                          border: "none",
                          padding: "4px 16px",
                          borderRadius: 8,
                          minHeight: "54px",
                        }),
                        input: (provided) => ({ ...provided, color: "#fff" }),
                        menuList: (provided) => ({ ...provided, color: "black" }),
                        singleValue: (provided) => ({ ...provided, color: "#fff" }),
                        valueContainer: (provided) => ({ ...provided, padding: "0px" }),
                      },
                    }}
                  />
                </SectionCard>

                <SectionCard title="Basic information">
                  <TwoCol>
                    <InputForm
                      placeholder="Name"
                      label="Display Name"
                      type="text"
                      id="venueName"
                      name="name"
                      value={updatedName}
                      onChange={(event) => setUpdatedName(event.target.value)}
                      error={errors.updatedName}
                    />
                    <SelectForm
                      label="Category"
                      isMulti
                      options={categoryOptions}
                      value={categoryOptions.filter(option => updatedCat.includes(option.value))}
                      onChange={handleCategoryChange}
                      error={errors.updatedCat}
                    />
                  </TwoCol>
                </SectionCard>

                <SectionCard title="Contact">
                  <TwoCol>
                    <InputForm
                      label="Phone"
                      type="text"
                      id="venuePhone"
                      name="phone"
                      value={updatedPhone}
                      onChange={(event) => setUpdatedPhone(event.target.value)}
                      placeholder="Enter phone"
                      error={errors.updatedPhone}
                    />
                    <InputForm
                      label="Website"
                      type="text"
                      id="venueSite"
                      name="url"
                      value={updatedUrl}
                      onChange={(event) => setUpdatedUrl(event.target.value)}
                      placeholder="Enter website"
                      error={errors.updatedUrl}
                    />
                  </TwoCol>
                  <div style={{ height: 16 }} />
                  <TwoCol>
                    <InputForm
                      label="E-mail"
                      type="email"
                      id="venueEmail"
                      name="email"
                      value={updatedEmail}
                      onChange={(event) => setUpdatedEmail(event.target.value)}
                      placeholder="Enter e-mail"
                      error={errors.updatedEmail}
                    />
                    <SelectForm
                      error={errors.updatedDress}
                      label="Dress-code"
                      name="dress"
                      options={dressCodeOptions}
                      value={dressCodeOptions.find(option => option.value === updatedDress) || null}
                      onChange={handleDressChange}
                      getOptionValue={(option) => option.value}
                    />
                  </TwoCol>
                </SectionCard>

                <SectionCard title="Music &amp; area">
                  <TwoCol>
                    <SelectForm
                      error={errors.updatedMusic}
                      label="Music"
                      isMulti
                      options={musicOptions}
                      value={musicOptions.filter((option) => updatedMusic.includes(option.value))}
                      onChange={handleMusicChange}
                    />
                    <SelectForm
                      error={errors.updatedMusic}
                      label="Location"
                      options={locationsOptions}
                      value={locationsOptions.find(opt => opt.value === updatedLocation)}
                      onChange={option => { if (option) setUpdatedLocation(option.value); }}
                    />
                  </TwoCol>
                </SectionCard>

                <SectionCard title="Photos">
                  <PhotoGallery
                    photos={venueImages}
                    onFilesPicked={handleFilesPicked}
                    onReorder={handleReorder}
                    onSetMain={handleSetMain}
                    onRemove={handleImageRemove}
                  />
                </SectionCard>

                <SectionCard title="About">
                  <label htmlFor="venueAbout" className="venue-info__form-group-field-label">
                    About
                    <span className='error-message'>{errors.updatedAbout}</span>
                  </label>
                  <textarea
                    placeholder='Description of the venue'
                    rows={4}
                    id="venueAbout"
                    name="description"
                    className="venue-info__form-group-about-input"
                    style={{ width: '100%' }}
                    value={updatedAbout}
                    onChange={(event) => setUpdatedAbout(event.target.value)}
                  />
                  <div style={{ height: 16 }} />
                  <label htmlFor="venueSpecials" className="venue-info__form-group-field-label">
                    More Information (Specials)
                    <span className='error-message'>{errors.updateSpecials}</span>
                  </label>
                  <textarea
                    placeholder='Description of the venue'
                    rows={4}
                    id="venueSpecials"
                    name="specials"
                    className="venue-info__form-group-about-input"
                    style={{ width: '100%' }}
                    value={updateSpecials}
                    onChange={(event) => setUpdateSpecials(event.target.value)}
                  />
                </SectionCard>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                  <Button
                    label={loading ? 'Sending Information...' : 'Save Information'}
                    type='submit'
                    iconifyIconName='material-symbols:check-rounded'
                    primaryBtn
                  />
                </div>

              </section>
            </form>
          </section>
        </article>
      </div>
    </main>
  );
};

export default AddVenue;
